<lib-headline [headline]="'aiProfileMatching.headline'"></lib-headline>

<mat-form-field class="ai-profile-matching__formfield">
  <mat-label>{{ 'aiProfileMatching.selectedLanguage' | translate }}</mat-label>
  <mat-select [(value)]="selectedLanguage">
    @for (lang of languages; track lang) {
      <mat-option [value]="lang.value">{{ lang.label | translate }}</mat-option>
    }
  </mat-select>
</mat-form-field>

@if (promptKeys.length > 0) {
  <mat-accordion>
    <mat-expansion-panel class="ai-profile-matching__prompt-panel">
      <mat-expansion-panel-header>
        <mat-panel-title>{{ 'aiProfileMatching.prompts.title' | translate }}</mat-panel-title>
      </mat-expansion-panel-header>

      <mat-form-field class="ai-profile-matching__formfield">
        <mat-label>{{ 'aiProfileMatching.prompts.selectPrompt' | translate }}</mat-label>
        <mat-select [(value)]="selectedPromptKey" (selectionChange)="onPromptSelected($event.value)">
          @for (key of promptKeys; track key) {
            <mat-option [value]="key">
              {{ 'aiProfileMatching.prompts.' + key | translate }}
              @if (isPromptModified(key)) {
                <span class="ai-profile-matching__prompt-modified"> *</span>
              }
            </mat-option>
          }
        </mat-select>
      </mat-form-field>

      @if (selectedPromptKey) {
        <mat-form-field class="ai-profile-matching__prompt-textarea" appearance="outline">
          <mat-label>{{ 'aiProfileMatching.prompts.' + selectedPromptKey | translate }}</mat-label>
          <textarea matInput
                    [(ngModel)]="promptOverrides[selectedPromptKey]"
                    cdkTextareaAutosize
                    cdkAutosizeMinRows="8"
                    cdkAutosizeMaxRows="25">
          </textarea>
        </mat-form-field>
        <div class="ai-profile-matching__prompt-actions">
          <button mat-stroked-button type="button" (click)="resetPrompt(selectedPromptKey)"
                  [disabled]="!isPromptModified(selectedPromptKey)">
            {{ 'aiProfileMatching.prompts.reset' | translate }}
          </button>
          <button mat-stroked-button type="button" (click)="resetAllPrompts()">
            {{ 'aiProfileMatching.prompts.resetAll' | translate }}
          </button>
        </div>
      }
    </mat-expansion-panel>
  </mat-accordion>
}

<div class="ai-profile-matching__container">
  @if (isLoading) {
    <div class="ai-profile-matching__status ai-profile-matching__status--loading">
      <mat-spinner diameter="32"></mat-spinner>
      <span>{{ 'aiProfileMatching.loading' | translate }}</span>
    </div>
  } @else if (loadError) {
    <div class="ai-profile-matching__status ai-profile-matching__status--error">
      <p>{{ 'aiProfileMatching.errorMessages.loadFailed' | translate }}</p>
      <button mat-stroked-button type="button" (click)="loadProfile()">
        {{ 'aiProfileMatching.preview.retry' | translate }}
      </button>
    </div>
  } @else if (aiExport) {
    <lib-subheadline [subheadline]="'aiProfileMatching.preview.sanitizedTitle'"></lib-subheadline>
    <div class="ai-profile-matching__info-container">
      <mat-icon class="ai-profile-matching__info-icon">info</mat-icon>
      <p class="ai-profile-matching__info-text">{{ 'aiProfileMatching.dsgvoText' | translate }}</p>
    </div>
    <div class="ai-profile-matching__content">
      <mat-card appearance="outlined" class="ai-profile-matching__preview-card">
        <pre class="ai-profile-matching__preview-content">{{ aiExport?.sanitized }}</pre>
      </mat-card>

      <div class="ai-profile-matching__upload">
        <input
          #jobFileInput
          type="file"
          accept=".txt,.json"
          class="ai-profile-matching__input"
          (change)="onJobFileSelected($event)"
        />
        <mat-card
          appearance="outlined"
          [ngClass]="{'ai-profile-matching__upload-card': true, 'ai-profile-matching__upload-card--selected': jobFileName}"
          tabindex="0"
          role="button"
          [attr.aria-label]="'aiProfileMatching.jobPosting' | translate"
          (click)="jobDescriptionButton()"
          (keydown.enter)="jobDescriptionButton()"
          (keydown.space)="jobDescriptionButton(); $event.preventDefault()"
        >
          @if (!jobFileName) {
            <mat-icon class="ai-profile-matching__upload-icon">upload_file</mat-icon>
          }
          <mat-card-header class="ai-profile-matching__upload-header">
            <mat-card-title class="ai-profile-matching__upload-title">
              {{ 'aiProfileMatching.jobPosting' | translate }}
            </mat-card-title>
            <mat-card-subtitle class="ai-profile-matching__upload-subtitle">
              @if (jobFileName) {
                <div>
                  {{ jobFileName }}
                </div>
              } @else {
                {{ 'aiProfileMatching.format' | translate }}
              }
            </mat-card-subtitle>
            @if (jobFileName) {
              <button
                type="button"
                class="ai-profile-matching__remove-file-button"
                (click)="removeJobFile($event)"
                attr.aria-label="{{ 'aiProfileMatching.deleteFile' | translate }}"
              >
                <mat-icon>delete</mat-icon>
              </button>
            }
          </mat-card-header>
        </mat-card>
      </div>
    </div>

    <button mat-flat-button type="button" (click)="startAnalyze()"
            [disabled]="analyzeState === 'processing' || !aiExport || !jobData">
      <span class="ai-profile-matching__button-content">
        @if (analyzeState === 'processing') {
          <mat-spinner diameter="20"></mat-spinner>
          {{ 'aiProfileMatching.analyzing' | translate }}
        } @else {
          {{ 'aiProfileMatching.startAnalyse' | translate }}
        }
      </span>
    </button>
  }
</div>

@if (analyzeState === 'complete' && analysis) {
  <mat-divider></mat-divider>
  <lib-subheadline [subheadline]="'aiProfileMatching.result'"></lib-subheadline>
  <lib-score-card
    [score]="analysis?.scores?.total"
    [maxScore]="100"
    [cardBackgroundColor]="'var(--primary-60)'"
    [labelColor]="'var(--primary-100)'"
    [scoreTextColor]="'var(--primary-100)'"
    label="aiProfileMatching.scoreCard.overall"
  >
  </lib-score-card>
  <div class="ai-profile-matching__score-container">
    <lib-score-card
      [score]="analysis?.scores?.qualifications"
      [maxScore]="20"
      [showBar]="true"
      label="aiProfileMatching.scoreCard.qualifications">
    </lib-score-card>
    <lib-score-card
      [score]="analysis?.scores?.cultural_fit"
      [maxScore]="20"
      [showBar]="true"
      label="aiProfileMatching.scoreCard.culturalFit">
    </lib-score-card>
    <lib-score-card
      [score]="analysis?.scores?.experience"
      [maxScore]="20"
      [showBar]="true"
      label="aiProfileMatching.scoreCard.experience">
    </lib-score-card>
    <lib-score-card
      [score]="analysis?.scores?.potential"
      [maxScore]="20"
      [showBar]="true"
      label="aiProfileMatching.scoreCard.potential">
    </lib-score-card>
    <lib-score-card
      [score]="analysis?.scores?.soft_skills"
      [maxScore]="20"
      [showBar]="true"
      label="aiProfileMatching.scoreCard.softSkills">
    </lib-score-card>
  </div>
  <app-analyze-result
    [strengths]="analysis?.analysis?.strengths"
    [weaknesses]="analysis?.analysis?.weaknesses"
    [gaps]="analysis?.analysis?.gaps"
    [matches]="analysis?.analysis?.matches"
    [recommendations]="analysis?.recommendations"
    [summary]="analysis?.summary"
  ></app-analyze-result>

  <mat-divider></mat-divider>

  <div class="ai-profile-matching__container">
    <button mat-flat-button type="button" (click)="startImprove()" [disabled]="improveState === 'processing'">
      <span class="ai-profile-matching__button-content">
        @if (improveState === 'processing') {
          <mat-spinner diameter="20"></mat-spinner>
          {{ 'aiProfileMatching.improving' | translate }}
        } @else {
          {{ 'aiProfileMatching.improveProfile' | translate }}
        }
      </span>
    </button>
  </div>
}

@if (improveState === 'complete') {
  <mat-divider></mat-divider>
  <app-improve-result
    [improvedProfile]="improvedProfile"
    [hunks]="hunks"
    [summary]="improveSummary"
    [isApplying]="isApplyingDecisions"
    [appliedProfile]="appliedProfile"
    (applyDecisions)="onApplyDecisions($event)"
    [consultantId]='consultantId'
  ></app-improve-result>
}
